import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Container } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DemoAssistantShell from '../../components/demo/DemoAssistantShell';
import HybridDemoGate from '../../components/demo/HybridDemoGate';
import ReturnsPortalPage from './ReturnsPortalPage';
import HiringPortalPage from './HiringPortalPage';
import PersonalAssistantPortalPage from './PersonalAssistantPortalPage';
import DeskContinuumPortalPage from './DeskContinuumPortalPage';
import { DEMO_ASSISTANT_META, getDemoAssistant } from '../../data/interviewFlowAssistantDemos';

const PORTAL_LINKS = Object.entries(DEMO_ASSISTANT_META).map(([key, meta]) => ({
  key,
  to: meta.path,
  label: meta.title.replace(/ advisor| pipeline| interview/gi, '').trim().split(' ')[0],
}));

/**
 * Public hybrid demos for CLAW Form→Flow→App assistants.
 * Route param :assistantKey → recruiting | returns | personal | realestate | listings
 * Also used with fixed `assistantKey` prop from dedicated routes.
 */
export default function StudioV2AssistantDemoPage({ assistantKey: assistantKeyProp = null }) {
  const { assistantKey: paramKey } = useParams();
  const key = assistantKeyProp || paramKey || 'returns';
  const meta = getDemoAssistant(key);

  const portalLinks = useMemo(
    () => PORTAL_LINKS.map((p) => ({
      to: p.to,
      label: DEMO_ASSISTANT_META[p.key].title.split(' ').slice(0, 2).join(' '),
      active: p.key === key,
    })),
    [key],
  );

  if (!meta) {
    return (
      <Container sx={{ py: 6 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Unknown demo assistant.
        </Alert>
        <Button component={RouterLink} to="/">Back to home</Button>
      </Container>
    );
  }

  return (
    <DemoAssistantShell
      definition={meta.definition}
      title={meta.title}
      subtitle={meta.subtitle}
      helpPath={meta.helpPath}
      primaryColor={meta.primaryColor}
      portalLinks={portalLinks}
      showOutcomeTray
    />
  );
}

export function StudioV2ReturnsDemoPage() {
  return (
    <HybridDemoGate demoId="returns">
      <ReturnsPortalPage />
    </HybridDemoGate>
  );
}
export function StudioV2PersonalDemoPage() {
  return (
    <HybridDemoGate demoId="personal">
      <PersonalAssistantPortalPage />
    </HybridDemoGate>
  );
}
export function StudioV2DeskDemoPage() {
  return (
    <HybridDemoGate demoId="desk">
      <DeskContinuumPortalPage />
    </HybridDemoGate>
  );
}
export function StudioV2RealEstateDemoPage() {
  return (
    <HybridDemoGate demoId="realestate">
      <StudioV2AssistantDemoPage assistantKey="realestate" />
    </HybridDemoGate>
  );
}
export function StudioV2ListingsDemoPage() {
  return (
    <HybridDemoGate demoId="listings">
      <StudioV2AssistantDemoPage assistantKey="listings" />
    </HybridDemoGate>
  );
}
export function StudioV2RecruitingAdvisorDemoPage() {
  return (
    <HybridDemoGate demoId="recruiting">
      <HiringPortalPage />
    </HybridDemoGate>
  );
}
